import {
    RegimeMapping,
    RegimeMappings,
    ResolvedRegimeMappings
} from "@idwal/idwal-utilities/lib/types/RegimeMapping";

/**
 * The string a chip is both rendered from and looked up by: the descriptions
 * are keyed on it, so a tooltip only ever resolves while the two are built
 * the same way. Kept in one place so they cannot drift apart silently.
 */
const toChipKey = ({ regime, code }: RegimeMapping) => `${regime}:${code}`;

/**
 * Presentation order for the chips shown against a defect: presentation rank
 * first (1 shows first), then the regime code numerically. Codes are typed as
 * strings, so the compare is numeric-aware rather than `Number(...)`, which
 * would yield NaN and an arbitrary order for a non-numeric code.
 */
const byPresentationOrder = (a: RegimeMapping, b: RegimeMapping) =>
    a.order - b.order ||
    a.code.localeCompare(b.code, undefined, { numeric: true });

/**
 * Flattens the regime mappings into the `{regime}:{code}` strings the shared
 * component library's defect code chips expect, in the order they are shown.
 * The Idwal defect code itself is prepended by the chips component, so it is
 * deliberately not included here.
 */
export const toDefectCodeChips = (
    mappings: RegimeMappings | undefined
): Record<string, string[]> | undefined => {
    if (!mappings) {
        return undefined;
    }

    const chips: Record<string, string[]> = {};

    for (const idwalCode of Object.keys(mappings)) {
        chips[idwalCode] = [...mappings[idwalCode]]
            .sort(byPresentationOrder)
            .map(toChipKey);
    }

    return chips;
};

/**
 * Collects what each regime code is into the `{regime}:{code}` keyed lookup
 * the chips read their tooltips from. Keyed by regime code rather than by
 * Idwal defect code because a code means the same thing whichever defect
 * points at it, so the same name is shared rather than repeated.
 *
 * `name` is optional on the resolved shape: the mappings and the descriptions
 * are uploaded as two files, so a mapping can arrive before the description
 * it points at. Those are skipped, and that chip simply has no tooltip.
 *
 * Returns undefined when nothing carries a name, so a run that fell back to
 * the unresolved mappings renders exactly as it did before tooltips existed
 * rather than mounting a tooltip that can never have anything to show.
 */
export const toRegimeDescriptions = (
    mappings: ResolvedRegimeMappings | undefined
): Record<string, string> | undefined => {
    if (!mappings) {
        return undefined;
    }

    const descriptions: Record<string, string> = {};
    let found = false;

    for (const idwalCode of Object.keys(mappings)) {
        for (const mapping of mappings[idwalCode]) {
            if (!mapping.name) {
                continue;
            }

            descriptions[toChipKey(mapping)] = mapping.name;
            found = true;
        }
    }

    return found ? descriptions : undefined;
};
